import { lazy, Suspense, useEffect, useState } from "react";
import Link from "@/components/LocaleLink";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  ChevronDown, User, Users, Building2, Store,
  Camera, Image, MessageSquare, MessageCircle, Tag, Rss, FileText, Link2, Info,
  BarChart3, Share2, Database, Package, Calculator, HardHat, Receipt, Menu, ClipboardCheck, LayoutDashboard, ShieldAlert, CalendarClock, Send, Box, Clock, Bot, DollarSign
} from "lucide-react";
import BookDemoButton from "@/components/BookDemoButton";
import LanguageSelector from "@/components/LanguageSelector";

const HeaderMobileMenu = lazy(() => import("@/components/HeaderMobileMenu"));

interface HeaderProps {
  overlayHero?: boolean;
}

const Header = ({ overlayHero = false }: HeaderProps) => {
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const isRTL = i18n.language === 'ar';
  const isHomePage = /^\/(?:(?:en|ru|ar|hi)\/?)?$/.test(location.pathname);
  const useHeroHeader = (isHomePage || overlayHero) && !isScrolled;

  useEffect(() => {
    const updateHeader = () => setIsScrolled(window.scrollY > 24);
    updateHeader();
    window.addEventListener("scroll", updateHeader, { passive: true });
    return () => window.removeEventListener("scroll", updateHeader);
  }, []);

  const companySizes = [
    { icon: User, labelKey: "companySizes.small.label", descriptionKey: "companySizes.small.description", color: "text-blue-500", path: "/who-we-serve?size=small" },
    { icon: Users, labelKey: "companySizes.medium.label", descriptionKey: "companySizes.medium.description", color: "text-blue-500", path: "/who-we-serve?size=medium" },
    { icon: Building2, labelKey: "companySizes.enterprise.label", descriptionKey: "companySizes.enterprise.description", color: "text-blue-500", path: "/who-we-serve?size=enterprise" },
    { icon: Store, labelKey: "companySizes.franchise.label", descriptionKey: "companySizes.franchise.description", color: "text-blue-500", path: "/who-we-serve?size=franchise" },
  ];

  const coreFeatures = [
    { icon: Camera, labelKey: "features.photoVideo.label", descriptionKey: "features.photoVideo.description", color: "text-blue-500", href: "/construction-photo-documentation" },
    { icon: BarChart3, labelKey: "features.trackProgress.label", descriptionKey: "features.trackProgress.description", color: "text-blue-500", href: "/construction-progress-tracking" },
    { icon: FileText, labelKey: "features.reports.label", descriptionKey: "features.reports.description", color: "text-blue-500", href: "/construction-reports" },
    { icon: Calculator, labelKey: "features.aiEstimating.label", descriptionKey: "features.aiEstimating.description", color: "text-violet-500", href: "/ai-construction-estimating" },
    { icon: Rss, labelKey: "features.projectFeed.label", descriptionKey: "features.projectFeed.description", color: "text-pink-500", href: "/construction-daily-log" },
    
    { icon: Database, labelKey: "features.documentIntel.label", descriptionKey: "features.documentIntel.description", color: "text-purple-500", href: "/construction-document-management" },
    { icon: Bot, labelKey: "features.aiAssistant.label", descriptionKey: "features.aiAssistant.description", color: "text-cyan-500", href: "/ai-for-construction" },
  ];

  const advancedFeatures = [
    { icon: ShieldAlert, labelKey: "features.riskManagement.label", descriptionKey: "features.riskManagement.description", color: "text-red-500", href: "/construction-risk-management" },
    { icon: Receipt, labelKey: "features.trackPerformance.label", descriptionKey: "features.trackPerformance.description", color: "text-indigo-500", href: "/contractor-invoicing" },
    { icon: CalendarClock, labelKey: "features.galleries.label", descriptionKey: "features.galleries.description", color: "text-blue-500", href: "/construction-scheduling" },
    { icon: Share2, labelKey: "features.shareUpdates.label", descriptionKey: "features.shareUpdates.description", color: "text-green-500", href: "/share-updates" },
    { icon: ClipboardCheck, labelKey: "features.aiInspection.label", descriptionKey: "features.aiInspection.description", color: "text-emerald-500", href: "/punch-list-software" },
    { icon: HardHat, labelKey: "features.compliance.label", descriptionKey: "features.compliance.description", color: "text-amber-500", href: "/construction-compliance" },
  ];

  const topIntegrations = [
    { icon: MessageCircle, labelKey: "integrations.whatsapp", color: "text-green-500" },
    { icon: Receipt, labelKey: "integrations.zatca", color: "text-teal-500" },
    { icon: Clock, labelKey: "integrations.primavera", color: "text-blue-700" },
    { icon: Send, labelKey: "integrations.telegram", color: "text-blue-400" },
    { icon: MessageCircle, labelKey: "integrations.max", color: "text-cyan-500" },
    { icon: MessageCircle, labelKey: "integrations.line", color: "text-green-400" },
  ];

  const navItems = [
    { labelKey: "nav.features", hasDropdown: true, id: "features" },
    { labelKey: "nav.whoWeServe", hasDropdown: false, id: "who-we-serve" },
    { labelKey: "nav.pricing", hasDropdown: false, id: "pricing" },
    { labelKey: "nav.resources", hasDropdown: true, id: "resources" },
    { labelKey: "nav.contact", hasDropdown: false, id: "contact" },
  ];

  const resourcesItems = [
    { icon: Rss, labelKey: "nav.blog", descriptionKey: "resources.blogDescription", href: "/blog", color: "text-orange-500" },
    { icon: CalendarClock, labelKey: "nav.webinar", descriptionKey: "resources.webinarDescription", href: "/webinar", color: "text-blue-500" },
    { icon: Info, labelKey: "nav.aboutUs", descriptionKey: "resources.aboutUsDescription", href: "/about", color: "text-emerald-500" },
    { icon: DollarSign, labelKey: "nav.affiliate", descriptionKey: "resources.affiliateDescription", href: "/affiliate", color: "text-purple-500" },
  ];

  return (
    <header
      className={`fixed left-0 right-0 top-0 z-50 w-full border-b transition-[background-color,border-color,box-shadow] duration-300 ${
        useHeroHeader
          ? "border-transparent bg-transparent shadow-none"
          : "border-border/60 bg-nav shadow-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto relative flex items-center justify-between py-4 px-4 sm:px-6 lg:px-12">
        {/* Logo */}
        <Link 
          to="/" 
          className="flex items-center shrink-0 z-10"
          aria-label="Banamind — Home"
          itemScope
          itemType="https://schema.org/Organization"
        >
          <span 
            className={`text-2xl sm:text-3xl font-black tracking-tight transition-colors duration-300 ${useHeroHeader ? "text-white" : "text-nav-foreground"}`}
            style={{ fontFamily: "'Bebas Neue', 'Impact', 'Arial Narrow', sans-serif", letterSpacing: '0.05em' }}
            itemProp="name"
          >
            BANAMIND
          </span>
          <meta itemProp="url" content="https://banamind.ai" />
        </Link>

        {/* Navigation - absolutely centered */}
        <nav className="hidden lg:flex items-center gap-8 absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20" dir={isRTL ? 'rtl' : 'ltr'}>
          {navItems.map((item) => (
            <div
              key={item.id}
              className="relative"
              onMouseEnter={() => item.hasDropdown && setOpenDropdown(item.id)}
              onMouseLeave={() => {
                if (!item.hasDropdown || item.id === "resources") setOpenDropdown(null);
              }}
            >
              {(item.id === "pricing" || item.id === "contact" || item.id === "who-we-serve") ? (
                <Link
                  to={
                    item.id === "pricing" ? "/pricing" :
                    item.id === "who-we-serve" ? "/who-we-serve" :
                    "/contact"
                  }
                  className={`flex items-center gap-1 py-2 font-medium transition-colors ${
                    useHeroHeader ? "text-white/80 hover:text-white" : "text-nav-foreground/80 hover:text-nav-foreground"
                  }`}
                >
                  {t(item.labelKey)}
                </Link>
              ) : (
                <button
                  className={`flex items-center gap-1 py-2 font-medium transition-colors ${
                    useHeroHeader ? "text-white/80 hover:text-white" : "text-nav-foreground/80 hover:text-nav-foreground"
                  }`}
                >
                  {t(item.labelKey)}
                  {item.hasDropdown && (
                    <ChevronDown className={`w-4 h-4 transition-transform ${openDropdown === item.id ? "rotate-180" : ""}`} />
                  )}
                </button>
              )}
              {/* Invisible bridge to dropdown */}
              {item.hasDropdown && openDropdown === item.id && (
                <div className="absolute left-1/2 -translate-x-1/2 top-full w-[300%] h-6" />
              )}

              {item.id === "resources" && openDropdown === "resources" && (
                <div className="absolute left-1/2 top-[calc(100%+0.5rem)] z-[110] w-[410px] -translate-x-1/2 overflow-hidden rounded-2xl border border-black/10 bg-white p-3 text-left shadow-[0_24px_70px_-20px_rgba(0,0,0,0.38)] rtl:text-right">
                  <p className="px-3 pb-2 pt-2 text-xs font-semibold uppercase tracking-[0.12em] text-black/45">
                    {t('nav.resources')}
                  </p>
                  <div className="space-y-0.5">
                    {resourcesItems.map((resource) => {
                      const Icon = resource.icon;
                      return (
                        <Link
                          key={resource.labelKey}
                          to={resource.href}
                          className="group flex items-start gap-3 rounded-xl px-3 py-3 transition-colors hover:bg-black/[0.045] focus-visible:bg-black/[0.045] focus-visible:outline-none"
                          onClick={() => setOpenDropdown(null)}
                        >
                          <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/[0.08] text-primary transition-colors group-hover:bg-primary/[0.13]">
                            <Icon className="h-[18px] w-[18px]" strokeWidth={2} />
                          </div>
                          <div className="min-w-0">
                            <span className="block text-[15px] font-semibold leading-5 text-slate-900">
                              {t(resource.labelKey)}
                            </span>
                            <span className="mt-0.5 block text-[13px] leading-5 text-slate-500">
                              {t(resource.descriptionKey)}
                            </span>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* Mega dropdown for Features - outside nav for full width */}
        {openDropdown === "features" && (
          <div 
            className="fixed inset-x-0 top-[72px] z-[100] w-screen"
            onMouseEnter={() => setOpenDropdown("features")}
            onMouseLeave={() => setOpenDropdown(null)}
          >
            <div className="w-full bg-nav shadow-2xl border-t border-border">
              <div className="max-w-7xl mx-auto grid grid-cols-3 divide-x divide-border">
                {/* Core Features column */}
                <div className="p-6">
                  <h4 className="text-xs font-bold text-nav-foreground/50 tracking-wider mb-4">
                    {t('header.coreFeatures')}
                  </h4>
                  <div className="space-y-0.5">
                    {coreFeatures.map((feature) => {
                      const Icon = feature.icon;
                      return (
                        <Link
                          key={feature.labelKey}
                          to={feature.href}
                          className="flex items-start gap-3 p-2 rounded-lg hover:bg-nav-foreground/5 transition-colors group"
                          onClick={() => setOpenDropdown(null)}
                        >
                          <Icon className={`w-4 h-4 mt-0.5 ${feature.color}`} />
                          <div>
                            <span className="block text-sm font-medium text-blue-500 group-hover:text-blue-600">
                              {t(feature.labelKey)}
                            </span>
                            <span className="text-xs text-nav-foreground/60">
                              {t(feature.descriptionKey)}
                            </span>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </div>

                {/* Advanced Features column */}
                <div className="p-6">
                  <h4 className="text-xs font-bold text-nav-foreground/50 tracking-wider mb-4">
                    {t('header.advancedFeatures')}
                  </h4>
                  <div className="space-y-0.5">
                    {advancedFeatures.map((feature) => {
                      const Icon = feature.icon;
                      return (
                        <Link
                          key={feature.labelKey}
                          to={feature.href}
                          className="flex items-start gap-3 p-2 rounded-lg hover:bg-nav-foreground/5 transition-colors group"
                          onClick={() => setOpenDropdown(null)}
                        >
                          <Icon className={`w-4 h-4 mt-0.5 ${feature.color}`} />
                          <div>
                            <span className="block text-sm font-medium text-blue-500 group-hover:text-blue-600">
                              {t(feature.labelKey)}
                            </span>
                            <span className="text-xs text-nav-foreground/60">
                              {t(feature.descriptionKey)}
                            </span>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </div>

                {/* Integrations column */}
                <div className="p-6 bg-nav-foreground/[0.02]">
                  <h4 className="text-xs font-bold text-nav-foreground/50 tracking-wider mb-4">
                    {t('header.topIntegrations')}
                  </h4>
                  <div className="grid grid-cols-2 gap-3 mb-6">
                    {topIntegrations.map((integration) => {
                      const Icon = integration.icon;
                      return (
                        <div
                          key={integration.labelKey}
                          className="flex items-center gap-2 p-2 rounded-lg bg-card border border-border"
                        >
                          <Icon className={`w-4 h-4 ${integration.color}`} />
                          <span className="text-sm text-nav-foreground/80">
                            {t(integration.labelKey)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  
                  <Link
                    to="/integrations"
                    className="inline-flex items-center gap-2 text-sm font-medium text-blue-500 hover:text-blue-600"
                    onClick={() => setOpenDropdown(null)}
                  >
                    <Link2 className="w-4 h-4" />
                    {t('header.seeAllIntegrations')}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Language selector and Auth buttons */}
        <div className="flex items-center gap-2 sm:gap-3 lg:gap-4 shrink-0">
          <BookDemoButton className="hidden sm:inline-flex" buttonClassName="px-4 lg:px-8 py-3 lg:py-5 text-sm lg:text-base" />
          <LanguageSelector variant="desktop" inverted={useHeroHeader} />

          {/* Mobile Menu */}
          <Button
            variant="ghost"
            size="icon"
            className={`lg:hidden ${useHeroHeader ? "text-white hover:bg-white/10 hover:text-white" : "text-nav-foreground"}`}
            onClick={() => setMobileMenuOpen(true)}
          >
            <Menu className="h-6 w-6" />
            <span className="sr-only">Open menu</span>
          </Button>
          {mobileMenuOpen && (
            <Suspense fallback={null}>
              <HeaderMobileMenu
                open={mobileMenuOpen}
                onOpenChange={setMobileMenuOpen}
                coreFeatures={coreFeatures}
                advancedFeatures={advancedFeatures}
                isRTL={isRTL}
              />
            </Suspense>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
